import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { appVersion, isApiError } from '@/api/client';
import { homePathForRole, useAuth } from '@/auth';
import { BrandMark, Button, Card, Field, Spinner } from '@/ui';

import './Login.css';

/**
 * Staff sign-in: email and password.
 *
 * Not phone and SMS. Customers authenticate through the SMS gateway, which in
 * this deployment is a stub that logs the code to the server's console — a fine
 * thing for a developer and an impossible one for a driver at 6am. Back-office
 * credentials are therefore independent of it entirely.
 *
 * The error states are the ones the API actually returns:
 *   401 invalidCredentials — one message for a wrong address and a wrong
 *       password alike, because "no account with that email" is an enumeration
 *       oracle pointed at a dozen named people;
 *   403 staffInactive     — the account exists and has been switched off;
 *   429 loginLocked / rateLimited — too many attempts, and the server's message
 *       already names the wait in seconds, so it is rendered verbatim;
 *   422 validation        — field errors, rendered under the input that failed.
 * Anything else falls through to the server's message.
 */

/** A blank submit is caught here so the server's rate limiter is not spent on it. */
interface FormErrors {
  email?: string;
  password?: string;
}

export function Login() {
  const { status, staff, signIn } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'loading') {
    return <Spinner center label="Checking your session" />;
  }

  if (status === 'authenticated' && staff !== null) {
    // Back where they were headed when the session ran out, or to their own
    // half of the app. RequireRole put the path in router state, which cannot
    // carry an off-site URL the way a ?next= query parameter can.
    const fromState: unknown = location.state;
    const from =
      typeof fromState === 'object' &&
      fromState !== null &&
      'from' in fromState &&
      typeof fromState.from === 'string' &&
      fromState.from.startsWith('/')
        ? fromState.from
        : homePathForRole(staff.role);

    return <Navigate to={from} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting) return;

    const trimmedEmail = email.trim();
    const next: FormErrors = {};
    if (trimmedEmail === '') next.email = 'Enter your email address';
    if (password === '') next.password = 'Enter your password';

    setFieldErrors(next);
    setFormError(null);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      await signIn(trimmedEmail, password);
      // No navigate() here: the redirect above fires on the next render, which
      // keeps one place deciding where a signed-in person goes.
    } catch (error) {
      if (isApiError(error)) {
        const mapped = error.fieldMap();
        if (Object.keys(mapped).length > 0) {
          setFieldErrors({
            ...(mapped.email === undefined ? {} : { email: mapped.email }),
            ...(mapped.password === undefined ? {} : { password: mapped.password }),
          });
          // A 422 that named no field we render still needs saying out loud.
          if (mapped.email === undefined && mapped.password === undefined) {
            setFormError(error.message);
          }
        } else {
          setFormError(error.message);
        }
      } else {
        setFormError('Could not sign in. Try again.');
      }
      // The password field is cleared on any failure: the next attempt is a
      // fresh one, and a wrong password left in the box gets resubmitted.
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login">
      <div className="login__panel">
        <div className="login__brand">
          <BrandMark className="login__mark" size={44} />
          <div>
            <div className="login__wordmark">
              <span className="brand-go">Go</span> DryClean
            </div>
            <div className="login__kicker">Back office</div>
          </div>
        </div>

        <Card>
          <h1 className="login__title">Sign in</h1>
          <p className="login__lede">Staff accounts only. Customers use the Go DryClean app.</p>

          <form
            className="login__form"
            noValidate
            onSubmit={(event) => {
              void onSubmit(event);
            }}
          >
            {formError !== null && (
              <p className="login__alert" role="alert">
                {formError}
              </p>
            )}

            <Field
              label="Email address"
              type="email"
              name="email"
              value={email}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              autoFocus
              disabled={submitting}
              {...(fieldErrors.email === undefined ? {} : { error: fieldErrors.email })}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />

            <Field
              label="Password"
              type="password"
              name="password"
              value={password}
              autoComplete="current-password"
              disabled={submitting}
              {...(fieldErrors.password === undefined ? {} : { error: fieldErrors.password })}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />

            <Button type="submit" size="lg" fullWidth loading={submitting}>
              Sign in
            </Button>
          </form>
        </Card>

        <p className="login__footnote">
          Lost your password? Ask an administrator to reset it — there is no self-service reset for
          staff accounts. · v{appVersion}
        </p>
      </div>
    </main>
  );
}
